<script lang="ts">
import GenericButton from "@/Components/Generics/GenericButton.vue";
import MainPage from "@/Components/MainPage.vue";
import ScrollUtilities from "@/Helpers/ScrollUtilities";
import CompanyCard from "@/Pages/CompanySearch/CompanyCard.vue";
import { QualificationInput, QualificationsGroups } from "@/Types";
import { Company, CompanyId } from "@/Types/company";
import { defineComponent, PropType } from "vue";

export default defineComponent({
    components: {
        MainPage,
        GenericButton,
        CompanyCard,
    },
    props: {
        noCompanies: {
            type: Boolean,
            default: false,
        },
        companies: {
            type: Array as PropType<Company[]>,
            required: true,
        },
        qualificationGroups: {
            type: Array as PropType<QualificationsGroups>,
            required: true,
        },
        qualificationInput: {
            type: Array as PropType<QualificationInput>,
            required: true,
        },
        detailViewCompanyId: {
            type: Number as PropType<CompanyId>,
            default: null,
        },
    },
    emits: ["clickBackToSearch", "changeDetailViewCompanyId"],
    computed: {
        detailViewCompany() {
            if (!this.detailViewCompanyId) return null;

            return this.companies.find((c) => c.id === this.detailViewCompanyId);
        },
        breadCrumbTitles() {
            const titles: string[] = [];

            titles.push(this.$trans("navigation.nav_find"));
            titles.push(this.$trans("navigation.nav_find_results"));

            if (!!this.detailViewCompanyId) {
                titles.push(this.$trans("navigation.nav_find_results_detail"));
            }

            return titles;
        },
    },
    methods: {
        onClickDetails(companyId: CompanyId): void {
            this.$emit("changeDetailViewCompanyId", companyId);

            this.$nextTick(() => {
                ScrollUtilities.jumpToTop();
            });
        },
        onClickCloseDetails(): void {
            const prevCompanyId = this.detailViewCompanyId;
            this.$emit("changeDetailViewCompanyId", null);

            this.$nextTick(() => {
                ScrollUtilities.jumpToDivCenter(`company-card-${prevCompanyId}`, undefined, true);
            });
        },

        onClickBackToSearch(): void {
            this.$emit("clickBackToSearch");
        },
    },
});
</script>

<template>
    <MainPage :bread-crumb-titles="breadCrumbTitles" :title-id="'startOfCompanyList'" :gap="true">
        <!-- DETAIL VIEW -->
        <template v-if="detailViewCompany">
            <GenericButton v-if="detailViewCompany" :title="$trans('company.button_back_to_results')" :identifier="'arrow_left'" :omit-icon-when-s-m="false" @click="onClickCloseDetails" />

            <CompanyCard :company="detailViewCompany" :qualification-groups="qualificationGroups" :qualification-input="qualificationInput" :more-info="true" @click-details="onClickDetails(detailViewCompany.id)" />

            <GenericButton v-if="detailViewCompany" :title="$trans('company.button_back_to_results')" :identifier="'arrow_left'" :omit-icon-when-s-m="false" @click="onClickCloseDetails" />
        </template>

        <!-- LIST VIEW -->
        <template v-else>
            <GenericButton :title="$trans('company.button_back_to_search')" :identifier="'arrow_left'" :omit-icon-when-s-m="false" @click="onClickBackToSearch" />

            <div v-if="noCompanies" class="flex flex--width flex--v flex--valign-c flex--gap-20">
                <span class="text text--size-24 text--primary-b text--semi-bold text--center">{{ $trans("search.no-companies-headline") }}</span>
                <span class="text text--size-20 text--accent-gray-b text--center">{{ $trans("search.no-companies-subheadline") }}</span>
            </div>

            <template v-else>
                <CompanyCard
                    v-for="company in companies"
                    :key="company.id"
                    :qualification-groups="qualificationGroups"
                    :qualification-input="qualificationInput"
                    :company="company"
                    :more-info="!!detailViewCompany"
                    @click-details="onClickDetails(company.id)" />
            </template>
        </template>
    </MainPage>
</template>

<style lang="scss" scoped></style>
